import React, { useState, useEffect, useRef, useCallback } from 'react';
import { collectionGroup, query, orderBy, limit, getDocs, startAfter, DocumentSnapshot, QueryDocumentSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { MediaItem, MediaFile } from '../types';
import PostVideoTrimmer from '../components/PostVideoTrimmer';

interface TrimRange {
  from: string;
  to: string;
}

interface CrossPostData {
  isEnabled: boolean;
  storyCollection: boolean;
  reelsCollection: boolean;
  storyTrim?: TrimRange;
  reelTrim?: TrimRange;
  caption?: string;
}

const timeAgo = (date: any): string => {
  if (!date) return '';
  const now = Date.now();
  const ts = typeof date?.toMillis === 'function' ? date.toMillis() : typeof date === 'number' ? date : new Date(date).getTime();
  if (isNaN(ts)) return '';
  const diffMs = now - ts;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
};

const fmtTime = (seconds: number): string => {
  if (!seconds) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const getVideoUrl = (media: MediaFile): string => {
  const raw = media.downloadURL;
  if (!raw) return '';
  // External URLs returned as-is
  if (raw.includes('instagram') || raw.includes('cdninstagram') || !raw.includes('sellox')) {
    return raw;
  }
  return raw;
};

const getProductThumbnail = (item: MediaItem): string => {
  if (!item.media || item.media.length === 0) return item.url || '';
  const image = item.media.find(m => m.type === 'image');
  if (image) {
    const raw = image.downloadURL;
    if (raw.includes('instagram') || raw.includes('cdninstagram') || !raw.includes('sellox')) {
      return raw;
    }
    try {
      let path = new URL(raw).pathname;
      path = path.replace(/.*.com/, '');
      path = path.substring(3);
      return 'https://cdn.sellox.app/preview/' + path + '?auto=compress&width=200&height=250&fit=crop&w=365&h=512';
    } catch {
      return raw;
    }
  }
  return item.url || '';
};

// ── CrossPost Bottom Sheet ──────────────────────────────────────────────

interface CrossPostPanelProps {
  open: boolean;
  onClose: () => void;
  initial?: CrossPostData;
  docPath: string; // e.g. shops/xyz/products/abc
  item: MediaItem; // needed for video source and trimmer
  onSaved?: (crossPost: CrossPostData) => void; // notify parent so it can update local state
}

const CrossPostPanel: React.FC<CrossPostPanelProps> = ({ open, onClose, initial, docPath, item, onSaved }) => {
  const [storyEnabled, setStoryEnabled] = useState(initial?.storyCollection ?? false);
  const [reelsEnabled, setReelsEnabled] = useState(initial?.reelsCollection ?? false);
  const [storyFrom, setStoryFrom] = useState(initial?.storyTrim?.from ?? '');
  const [storyTo, setStoryTo] = useState(initial?.storyTrim?.to ?? '');
  const [reelFrom, setReelFrom] = useState(initial?.reelTrim?.from ?? '');
  const [reelTo, setReelTo] = useState(initial?.reelTrim?.to ?? '');
  const [caption, setCaption] = useState(initial?.caption ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Track which placement the user is manually editing (null = trimmer sets all active)
  const [editingPlacement, setEditingPlacement] = useState<'story' | 'reel' | null>(null);

  // Sync when initial data changes (switching between cards)
  useEffect(() => {
    setStoryEnabled(initial?.storyCollection ?? false);
    setReelsEnabled(initial?.reelsCollection ?? false);
    setStoryFrom(initial?.storyTrim?.from ?? '');
    setStoryTo(initial?.storyTrim?.to ?? '');
    setReelFrom(initial?.reelTrim?.from ?? '');
    setReelTo(initial?.reelTrim?.to ?? '');
    setCaption(initial?.caption ?? '');
    setSaved(false);
    setEditingPlacement(null);
  }, [initial, docPath]);

  // Live callback from trimmer — sets trim for all active placements as user drags
  const handleTrimChange = useCallback((startAt: number, endAt: number) => {
    const from = fmtTime(startAt);
    const to = fmtTime(endAt);
    if (storyEnabled) {
      setStoryFrom(from);
      setStoryTo(to);
    }
    if (reelsEnabled) {
      setReelFrom(from);
      setReelTo(to);
    }
  }, [storyEnabled, reelsEnabled]);

  const handleSave = async () => {
    if (!docPath) return;
    setSaving(true);
    try {
      const isEnabled = storyEnabled || reelsEnabled;
      const crossPost: CrossPostData = {
        isEnabled,
        storyCollection: storyEnabled,
        reelsCollection: reelsEnabled,
        ...(caption.trim() ? { caption: caption.trim() } : {}),
      };
      if (storyEnabled && (storyFrom || storyTo)) {
        crossPost.storyTrim = { from: storyFrom || '0:00', to: storyTo || '0:00' };
      }
      if (reelsEnabled && (reelFrom || reelTo)) {
        crossPost.reelTrim = { from: reelFrom || '0:00', to: reelTo || '0:00' };
      }

      const ref = doc(db, docPath);
      await updateDoc(ref, { crossPost });
      onSaved?.(crossPost);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onClose();
      }, 800);
    } catch (err) {
      console.error('Failed to save crossPost:', err);
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-gray-50 flex flex-col" onClick={(e) => e.stopPropagation()}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
        <button onClick={onClose} className="text-gray-600 hover:text-gray-900">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <h3 className="text-base font-semibold text-gray-900">Cross-Post</h3>
        <button
          onClick={handleSave}
          disabled={saving || (!storyEnabled && !reelsEnabled)}
          className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${saved
            ? 'bg-green-500 text-white'
            : 'bg-gray-900 text-white hover:bg-gray-800 active:scale-95'
            } disabled:opacity-40`}
        >
          {saving ? 'Saving...' : saved ? 'Saved \u2713' : 'Save'}
        </button>
      </div>

      {/* Trimmer */}
      <div className="flex-1 mx-auto max-w-lg overflow-auto">
        <div className="bg-black">
          <PostVideoTrimmer
            item={item}
            onSave={() => { }} // not used in inline mode
            onClose={onClose}
            inline
            onTrimChange={handleTrimChange}
          />
        </div>

        {/* Placement controls */}
        <div className="px-4 py-4 space-y-3">
          {/* ── Story ── */}
          <div className={`rounded-xl border transition-colors ${storyEnabled ? 'border-pink-200 bg-pink-50/50' : 'border-gray-200 bg-white'
            }`}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3" />
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={2} fill="none" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-gray-800 text-sm">Story</span>
                  {storyEnabled && storyFrom && storyTo && (
                    <div className="text-[11px] text-gray-500">{storyFrom} \u2192 {storyTo}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setStoryEnabled(!storyEnabled)}
                className={`relative w-11 h-6 rounded-full transition-colors ${storyEnabled ? 'bg-pink-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${storyEnabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {storyEnabled && (
              <div className="px-4 pb-3 flex items-center gap-2">
                <button
                  onClick={() => setEditingPlacement(editingPlacement === 'story' ? null : 'story')}
                  className="text-[11px] text-pink-600 hover:text-pink-700 font-medium"
                >
                  {editingPlacement === 'story' ? 'Done' : 'Edit trim'}
                </button>
                {editingPlacement === 'story' && (
                  <div className="flex items-center gap-1.5 ml-1">
                    <input
                      type="text"
                      placeholder="0:00"
                      value={storyFrom}
                      onChange={(e) => setStoryFrom(e.target.value)}
                      className="w-14 px-1.5 py-1 text-xs border border-pink-300 rounded-lg text-center focus:ring-2 focus:ring-pink-400 focus:border-pink-400 outline-none"
                    />
                    <span className="text-gray-400 text-[10px]">to</span>
                    <input
                      type="text"
                      placeholder="0:00"
                      value={storyTo}
                      onChange={(e) => setStoryTo(e.target.value)}
                      className="w-14 px-1.5 py-1 text-xs border border-pink-300 rounded-lg text-center focus:ring-2 focus:ring-pink-400 focus:border-pink-400 outline-none"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Reels ── */}
          <div className={`rounded-xl border transition-colors ${reelsEnabled ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-white'
            }`}>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <span className="font-medium text-gray-800 text-sm">Reels</span>
                  {reelsEnabled && reelFrom && reelTo && (
                    <div className="text-[11px] text-gray-500">{reelFrom} \u2192 {reelTo}</div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setReelsEnabled(!reelsEnabled)}
                className={`relative w-11 h-6 rounded-full transition-colors ${reelsEnabled ? 'bg-blue-500' : 'bg-gray-300'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${reelsEnabled ? 'translate-x-5' : ''}`} />
              </button>
            </div>
            {reelsEnabled && (
              <div className="px-4 pb-3 flex items-center gap-2">
                <button
                  onClick={() => setEditingPlacement(editingPlacement === 'reel' ? null : 'reel')}
                  className="text-[11px] text-blue-600 hover:text-blue-700 font-medium"
                >
                  {editingPlacement === 'reel' ? 'Done' : 'Edit trim'}
                </button>
                {editingPlacement === 'reel' && (
                  <div className="flex items-center gap-1.5 ml-1">
                    <input
                      type="text"
                      placeholder="0:00"
                      value={reelFrom}
                      onChange={(e) => setReelFrom(e.target.value)}
                      className="w-14 px-1.5 py-1 text-xs border border-blue-300 rounded-lg text-center focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                    />
                    <span className="text-gray-400 text-[10px]">to</span>
                    <input
                      type="text"
                      placeholder="0:00"
                      value={reelTo}
                      onChange={(e) => setReelTo(e.target.value)}
                      className="w-14 px-1.5 py-1 text-xs border border-blue-300 rounded-lg text-center focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none"
                    />
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Caption ── */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="px-4 py-3">
              <label className="block text-sm font-medium text-gray-800 mb-1.5">Caption</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                placeholder="Write a caption for your reel..."
                rows={3}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-400 focus:border-gray-400 outline-none resize-none placeholder:text-gray-400"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Reel Card ───────────────────────────────────────────────────────────

interface ReelCardProps {
  item: MediaItem;
  videoMedia: MediaFile;
  isVisible: boolean;
  shouldLoad: boolean; // only load video src when true (visible ±1)
  onCrossPostSaved?: (docPath: string, crossPost: CrossPostData) => void;
}

const ReelCard: React.FC<ReelCardProps> = ({ item, videoMedia, isVisible, shouldLoad, onCrossPostSaved }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [showPoster, setShowPoster] = useState(true);
  const [crossPostOpen, setCrossPostOpen] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isVisible) {
      video.play().then(() => {
        setIsPlaying(true);
        setShowPoster(false);
      }).catch(() => {
        // Autoplay blocked - user needs to interact
        setIsPlaying(false);
      });
    } else {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
      setShowPoster(true);
    }
  }, [isVisible]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play().then(() => setIsPlaying(true)).catch(() => { });
    } else {
      video.pause();
      setIsPlaying(false);
    }
  };

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;
    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const thumbnail = getProductThumbnail(item);

  return (
    <div
      className="relative w-full bg-black rounded-2xl overflow-hidden snap-start snap-always"
      style={{ height: 'calc(100vh - 120px)', maxHeight: '800px', minHeight: '500px' }}
      onClick={togglePlay}
    >
      {/* Video — only load src when within ±1 of visible */}
      <video
        ref={videoRef}
        src={shouldLoad ? getVideoUrl(videoMedia) : undefined}
        className="absolute inset-0 w-full h-full object-contain bg-black"
        loop
        muted={isMuted}
        playsInline
        preload={shouldLoad ? 'metadata' : 'none'}
        poster={thumbnail || undefined}
      />

      {/* Poster overlay while not playing */}
      {showPoster && thumbnail && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/30 z-10">
          <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}

      {/* Pause indicator */}
      {!isPlaying && !showPoster && (
        <div className="absolute inset-0 flex items-center justify-center z-10">
          <div className="w-16 h-16 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
            </svg>
          </div>
        </div>
      )}

      {/* Right-side action bar */}
      <div className="absolute right-3 top-1/2 -translate-y-1/2 z-20 flex flex-col items-center gap-4">
        {/* Mute */}
        <button
          onClick={toggleMute}
          className="w-11 h-11 bg-black/40 backdrop-blur-sm rounded-full flex items-center justify-center"
        >
          {isMuted ? (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
            </svg>
          ) : (
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
            </svg>
          )}
        </button>

        {/* Cross-Post button (includes trimmer) */}
        <button
          onClick={(e) => { e.stopPropagation(); setCrossPostOpen(true); }}
          className={`w-11 h-11 backdrop-blur-sm rounded-full flex items-center justify-center transition-colors ${(item as any).crossPost?.isEnabled
            ? 'bg-pink-500/80'
            : 'bg-black/40'
            }`}
        >
          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
        </button>
      </div>

      {/* Bottom info */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-4 pb-4 pt-12">
        {(item.name || item.createdAt) && (
          <div className="mb-2">
            {item.name && <p className="text-white text-sm font-semibold truncate">{item.name}</p>}
            {item.createdAt && <p className="text-white/60 text-xs">{timeAgo(item.createdAt)}</p>}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          {(item as any).crossPost?.isEnabled && (
            <>
              {(item as any).crossPost.storyCollection && (
                <span className="px-2 py-0.5 bg-pink-500/80 text-white text-[10px] font-medium rounded-full">Story</span>
              )}
              {(item as any).crossPost.reelsCollection && (
                <span className="px-2 py-0.5 bg-blue-500/80 text-white text-[10px] font-medium rounded-full">Reels</span>
              )}
            </>
          )}
          {(item as any).crossPost?.storyTrim && (
            <span className="px-2 py-0.5 bg-white/20 text-white text-[10px] font-medium rounded-full">
              S: {(item as any).crossPost.storyTrim.from} → {(item as any).crossPost.storyTrim.to}
            </span>
          )}
          {(item as any).crossPost?.reelTrim && (
            <span className="px-2 py-0.5 bg-white/20 text-white text-[10px] font-medium rounded-full">
              R: {(item as any).crossPost.reelTrim.from} → {(item as any).crossPost.reelTrim.to}
            </span>
          )}
        </div>
      </div>

      {/* CrossPost bottom sheet */}
      <CrossPostPanel
        open={crossPostOpen}
        onClose={() => setCrossPostOpen(false)}
        initial={(item as any).crossPost}
        docPath={item.docPath || ''}
        item={item}
        onSaved={(cp) => onCrossPostSaved?.(item.docPath || '', cp)}
      />
    </div>
  );
};

type ProductWithVideo = MediaItem & { videoMedia: MediaFile };

const PAGE_SIZE = 20; // video products per page (we fetch more raw docs to filter)
const FETCH_MULTIPLIER = 4; // fetch 4x to account for non-video products

const ReelsPage: React.FC = () => {
  const [products, setProducts] = useState<ProductWithVideo[]>([]);
  const [loading, setLoading] = useState(true);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  // Stack of lastDoc cursors for each page boundary so we can go back
  const cursorStack = useRef<DocumentSnapshot[]>([]);

  const fetchPage = useCallback(async (afterDoc?: DocumentSnapshot) => {
    try {
      const constraints = [
        collectionGroup(db, 'products'),
        orderBy('createdAt', 'desc'),
        ...(afterDoc ? [startAfter(afterDoc)] : []),
        limit(PAGE_SIZE * FETCH_MULTIPLIER),
      ];
      // @ts-ignore — spread into query
      const q = query(...constraints);

      const snapshot = await getDocs(q);
      const items: ProductWithVideo[] = [];

      snapshot.forEach((d: QueryDocumentSnapshot) => {
        if (items.length >= PAGE_SIZE) return; // cap at PAGE_SIZE videos
        const data = d.data();
        const media: MediaFile[] = data.media || [];
        const videoMedia = media.find(m => m.type === 'video');
        if (!videoMedia || !videoMedia.downloadURL) return;

        items.push({
          id: d.id,
          docPath: d.ref.path,
          ...data,
          media,
          videoMedia,
        } as ProductWithVideo);
      });

      const lastVisible = snapshot.docs[snapshot.docs.length - 1] || null;
      return { items, lastVisible, rawCount: snapshot.docs.length };
    } catch (error) {
      console.error('Error fetching products:', error);
      return { items: [], lastVisible: null, rawCount: 0 };
    }
  }, []);

  // Initial load
  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { items, lastVisible, rawCount } = await fetchPage();
      setProducts(items);
      setHasMore(rawCount >= PAGE_SIZE); // may have more
      if (lastVisible) cursorStack.current = [lastVisible];
      setPageNum(1);
      setVisibleIndex(0);
      setLoading(false);
    };
    load();
  }, [fetchPage]);

  const goNextPage = async () => {
    const lastCursor = cursorStack.current[cursorStack.current.length - 1];
    if (!lastCursor) return;
    setLoading(true);
    const { items, lastVisible, rawCount } = await fetchPage(lastCursor);
    if (items.length === 0) {
      setHasMore(false);
      setLoading(false);
      return;
    }
    setProducts(items);
    setHasMore(rawCount >= PAGE_SIZE);
    if (lastVisible) cursorStack.current.push(lastVisible);
    setPageNum(prev => prev + 1);
    setVisibleIndex(0);
    setLoading(false);
    // Scroll to top
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  const goPrevPage = async () => {
    if (pageNum <= 1) return;
    // Pop the current page's cursor and the previous page's cursor
    cursorStack.current.pop(); // remove current
    const prevCursor = cursorStack.current.length >= 2
      ? cursorStack.current[cursorStack.current.length - 2]
      : undefined;
    // Remove the one we're about to re-fetch from so fetchPage pushes it back
    if (prevCursor) cursorStack.current.pop();
    setLoading(true);
    const { items, lastVisible, rawCount } = await fetchPage(prevCursor);
    setProducts(items);
    setHasMore(rawCount >= PAGE_SIZE);
    if (lastVisible) cursorStack.current.push(lastVisible);
    setPageNum(prev => Math.max(1, prev - 1));
    setVisibleIndex(0);
    setLoading(false);
    scrollContainerRef.current?.scrollTo({ top: 0 });
  };

  // IntersectionObserver for scroll-based visibility tracking (within current page)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = Number(entry.target.getAttribute('data-index'));
            if (!isNaN(index)) {
              setVisibleIndex(index);
            }
          }
        });
      },
      { threshold: 0.6 }
    );

    cardRefs.current.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [products]);

  const setCardRef = useCallback((index: number, el: HTMLDivElement | null) => {
    if (el) {
      cardRefs.current.set(index, el);
    } else {
      cardRefs.current.delete(index);
    }
  }, []);

  // Update local item state when crossPost is saved from the panel
  const handleCrossPostSaved = useCallback((docPath: string, crossPost: CrossPostData) => {
    setProducts(prev => prev.map(p =>
      p.docPath === docPath ? { ...p, crossPost } as any : p
    ));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (products.length === 0 && pageNum === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Reels</h3>
          <p className="text-gray-500">No products with videos found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto">
      {/* Top bar: counter + pagination */}
      <div className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm px-4 py-2 flex items-center justify-between border-b border-gray-100">
        <span className="text-sm font-medium text-gray-700">
          {visibleIndex + 1} / {products.length} &middot; Page {pageNum}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={goPrevPage}
            disabled={pageNum <= 1}
            className="px-3 py-1.5 text-xs font-medium rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            &larr; Prev
          </button>
          <button
            onClick={goNextPage}
            disabled={!hasMore}
            className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next &rarr;
          </button>
        </div>
      </div>

      {/* Vertical scroll feed */}
      <div
        ref={scrollContainerRef}
        className="snap-y snap-mandatory overflow-y-auto space-y-3 p-3"
        style={{ height: 'calc(100vh - 120px)' }}
      >
        {products.map((item, index) => (
          <div
            key={`${item.id}-${pageNum}-${index}`}
            ref={(el) => setCardRef(index, el)}
            data-index={index}
          >
            <ReelCard
              item={item}
              videoMedia={item.videoMedia}
              isVisible={index === visibleIndex}
              shouldLoad={Math.abs(index - visibleIndex) <= 1}
              onCrossPostSaved={handleCrossPostSaved}
            />
          </div>
        ))}

        {/* Bottom pagination (easy tap at end of scroll) */}
        <div className="flex justify-center gap-3 py-6">
          <button
            onClick={goPrevPage}
            disabled={pageNum <= 1}
            className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            &larr; Previous Page
          </button>
          <button
            onClick={goNextPage}
            disabled={!hasMore}
            className="px-5 py-2.5 text-sm font-medium rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          >
            Next Page &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReelsPage;
